<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import FileUploadField from '../components/FileUploadField.vue'
import ColorPickerModal from '../components/ColorPickerModal.vue'
import EquipmentPickerModal from '../components/EquipmentPickerModal.vue'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import PersonnelPickerModal from '../components/PersonnelPickerModal.vue'
import ThreadCodePickerModal from '../components/ThreadCodePickerModal.vue'
import SockPassportDimensionsTable from '../components/SockPassportDimensionsTable.vue'
import SockPassportElementsTable from '../components/SockPassportElementsTable.vue'
import SockPassportYarnsTable from '../components/SockPassportYarnsTable.vue'
import { defaultSockElements, defaultSockYarns } from '../sockPassportStructure'
import { restoreDecorativeElement, serializeDecorativeElement } from '../sockDecorations'
import { applyBomToPassport } from '../bomPassportLink'
import { resolveAssetUrl } from '../productPresentation'
import {
  createTechnicalPassport,
  getProduct,
  getTechnicalPassport,
  getTechnicalPassportDictionaries,
  getBomVersion,
  listEquipmentReferences,
  listColorReferences,
  listThreadCodes,
  listPersonnelEmployees,
  listProducts,
  listProductBomVersions,
  updateTechnicalPassport,
} from '../productService'
import type {
  BomVersion,
  EquipmentReference,
  ColorReference,
  EngineeringProduct,
  ProductionEmployee,
  ThreadCodeReference,
  TechnicalPassport,
  TechnicalPassportDictionaryOption,
  TechnicalPassportPayload,
  TechnicalPassportStatus,
  TechnicalPassportMaterial,
  TechnicalPassportMachine,
  TechnicalPassportMeasurement,
  TechnicalPassportPackaging,
  TechnicalPassportQcItem,
  TechnicalPassportLabel,
  TechnicalPassportThread,
  TechnicalPassportCosting,
  TechnicalPassportAttachment,
  TechnicalPassportRoute,
  TechnicalPassportDimensions,
  TechnicalPassportElement,
  TechnicalPassportYarn,
} from '../types'

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

const route = useRoute()
const router = useRouter()
const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()
const passportId = computed(() => Number(route.params.id))
const isEditing = computed(() => Number.isInteger(passportId.value) && passportId.value > 0)
const productId = computed(() => Number(route.query.product_id))
const product = ref<EngineeringProduct | null>(null)
const passport = ref<TechnicalPassport | null>(null)
const activeBom = ref<BomVersion | null>(null)
const templateTypes = ref<TechnicalPassportDictionaryOption[]>([])
const equipmentReferences = ref<EquipmentReference[]>([])
const approvalPeople = ref<ProductionEmployee[]>([])
const yarnMaterials = ref<EngineeringProduct[]>([])
const colorReferences = ref<ColorReference[]>([])
const threadCodeReferences = ref<ThreadCodeReference[]>([])
const pendingMainProductImageBase64 = ref('')
const pendingMainProductImagePreview = ref('')
const pendingTechnicalDrawingBase64 = ref('')
const pendingTechnicalDrawingPreview = ref('')
const mainProductImageStatus = ref('')
const technicalDrawingStatus = ref('')
const loading = ref(false)
const saving = ref(false)
const loadError = ref('')
const submitError = ref('')
const fieldErrors = reactive<Record<string, string>>({})
const initialFormState = ref<Record<string, unknown> | null>(null)
type PassportTab = 'general' | 'approval' | 'structure' | 'materials' | 'route' | 'measurements' | 'machines' | 'quality' | 'packaging' | 'labels' | 'threads' | 'costing' | 'attachments' | 'revision'
const activeTab = ref<PassportTab>('general')
const tabs = computed<Array<{ id: PassportTab; label: string; prefixes: string[]; fields: string[] }>>(() => [
  { id: 'general', label: t('engineering.passportForm.general'), prefixes: ['product', 'template_', 'sock_', 'legacy_rubber_return_place', 'legacy_cotton_yarn', 'legacy_spandex_yarn', 'legacy_rubber_yarn', 'legacy_nylon_yarn'], fields: ['template_type', 'sock_type', 'season', 'size_range', 'sock_weight_gram', 'description', 'prepared_by', 'rubber_return_place', 'cotton_yarn', 'spandex_yarn', 'rubber_yarn', 'nylon_yarn'] },
  { id: 'approval', label: t('engineering.passportForm.approvalTab'), prefixes: ['technologist_', 'shift_manager_', 'manager_'], fields: ['technologist_id', 'shift_manager_id', 'manager_id', 'technologist_approved_at', 'shift_manager_approved_at', 'manager_approved_at'] },
  { id: 'structure', label: t('engineering.passportForm.sockStructure'), prefixes: ['dimension_', 'element_', 'yarn_'], fields: ['dimensions', 'elements', 'yarns'] },
  { id: 'materials', label: t('engineering.passportForm.materials'), prefixes: ['material_'], fields: ['raw_materials'] },
  { id: 'route', label: t('engineering.passportForm.route'), prefixes: ['route_'], fields: ['routes'] },
  { id: 'measurements', label: t('engineering.passportForm.measurements'), prefixes: ['measurement_'], fields: ['measurements'] },
  { id: 'machines', label: t('engineering.passportForm.machines'), prefixes: ['machine_'], fields: ['machines'] },
  { id: 'quality', label: t('engineering.passportForm.quality'), prefixes: ['qc_'], fields: ['qc_items'] },
  { id: 'packaging', label: t('engineering.passportForm.packaging'), prefixes: ['packaging_'], fields: ['packaging'] },
  { id: 'labels', label: t('engineering.passportForm.labels'), prefixes: ['label_'], fields: ['labels'] },
  { id: 'threads', label: t('engineering.passportForm.threads'), prefixes: ['thread_'], fields: ['threads'] },
  { id: 'costing', label: t('engineering.passportForm.costing'), prefixes: ['costing_'], fields: ['costing'] },
  { id: 'attachments', label: t('engineering.passportForm.attachments'), prefixes: ['legacy_main_product_image', 'legacy_technical_drawing_image', 'legacy_knitting_machine_design_files'], fields: ['attachments', 'main_product_image', 'technical_drawing_image'] },
  { id: 'revision', label: t('engineering.passportForm.revision'), prefixes: ['revision_', 'changed_', 'change_'], fields: ['revision_no', 'revision_date', 'changed_by', 'change_description'] },
])
function tabHasErrors(tab: (typeof tabs.value)[number]): boolean {
  return Object.keys(fieldErrors).some((key) => tab.prefixes.some((prefix) => key.startsWith(prefix)))
}
function tabHasChanges(tab: (typeof tabs.value)[number]): boolean {
  if (!initialFormState.value) return false
  return tab.fields.some((field) =>
    JSON.stringify((form as Record<string, unknown>)[field])
    !== JSON.stringify(initialFormState.value?.[field]),
  )
}

const fieldTabMap: Partial<Record<string, PassportTab>> = {
  rubber_return_place: 'general',
  cotton_yarn: 'general',
  spandex_yarn: 'general',
  rubber_yarn: 'general',
  nylon_yarn: 'general',
  main_product_image: 'attachments',
  technical_drawing_image: 'attachments',
  knitting_machine_design_files: 'attachments',
  technologist_id: 'approval',
  shift_manager_id: 'approval',
  manager_id: 'approval',
}

function passportFieldLabel(field: string): string {
  const labelKey = {
    rubber_return_place: 'engineering.passportForm.rubberReturnPlace',
    cotton_yarn: 'engineering.passportForm.cottonYarn',
    spandex_yarn: 'engineering.passportForm.spandexYarn',
    rubber_yarn: 'engineering.passportForm.rubberYarn',
    nylon_yarn: 'engineering.passportForm.nylonYarn',
    main_product_image: 'engineering.passportForm.mainProductImage',
    technical_drawing_image: 'engineering.passportForm.technicalDrawingImage',
    knitting_machine_design_files: 'engineering.passportForm.knittingMachineDesignFiles',
    technologist_id: 'engineering.passportDetail.technologist',
    shift_manager_id: 'engineering.passportDetail.shiftManager',
    manager_id: 'engineering.passportDetail.manager',
  } as const
  const key = labelKey[field as keyof typeof labelKey]
  return key ? t(key) : field
}

function passportTabLabel(tab: PassportTab): string {
  return tabs.value.find((item) => item.id === tab)?.label ?? tab
}

const approvalRecommendations = computed<string[]>(() => {
  const recommendations: string[] = []
  if (!form.technologist_id) recommendations.push(t('engineering.passportForm.approvalFixTechnologist'))
  if (!form.shift_manager_id) recommendations.push(t('engineering.passportForm.approvalFixShiftManager'))
  if (!form.manager_id) recommendations.push(t('engineering.passportForm.approvalFixManager'))
  if (fieldErrors.machine_0) recommendations.push(t('engineering.passportForm.approvalFixMachine'))
  if (fieldErrors.change_description) recommendations.push(t('engineering.passportForm.approvalFixRevision'))
  if (!recommendations.length && submitError.value) recommendations.push(t('engineering.passportForm.approvalRetry'))
  return recommendations
})

const approvalErrorTitle = computed(() =>
  submitError.value
    ? t('engineering.passportForm.approvalApiError')
    : Object.keys(fieldErrors).length
      ? t('engineering.passportForm.approvalValidationError')
      : '',
)
function resolvedAttachmentUrl(value?: string | null): string {
  return resolveAssetUrl(value ?? null) ?? ''
}
const productPicker = reactive({
  open: false,
  target: '' as 'material' | 'packaging' | 'label' | '',
  index: -1,
})
const personnelPicker = reactive({
  open: false,
  target: '' as 'technologist' | 'shift_manager' | 'manager' | '',
})
const threadColorPicker = reactive({
  open: false,
  index: -1,
})
const threadCodePicker = reactive({
  open: false,
  index: -1,
})
const machinePicker = reactive({
  open: false,
  index: -1,
})
const initialDimensions: TechnicalPassportDimensions = {
  foot_length_mm: 250,
  leg_height_mm: 150,
  foot_width_mm: 90,
  cuff_height_mm: 35,
  cuff_width_mm: 80,
  toe_length_mm: 45,
  heel_length_mm: 60,
  instep_length_mm: 80,
}
const form = reactive({
  template_type: '',
  sock_type: '' as '' | 'uzun' | 'kalta' | 'sledi',
  season: '',
  size_range: '',
  sock_weight_gram: 66,
  description: '',
  prepared_by: '',
  rubber_return_place: '',
  cotton_yarn: '',
  spandex_yarn: '',
  rubber_yarn: '',
  nylon_yarn: '',
  main_product_image: '',
  technical_drawing_image: '',
  technologist_id: undefined as number | undefined,
  shift_manager_id: undefined as number | undefined,
  manager_id: undefined as number | undefined,
  technologist_approved_at: '',
  shift_manager_approved_at: '',
  manager_approved_at: '',
  status: 'draft' as TechnicalPassportStatus,
  reject_reason: '',
  revision_no: '1',
  revision_date: new Date().toISOString().slice(0, 10),
  changed_by: '',
  change_description: '',
  raw_materials: [] as TechnicalPassportMaterial[],
  routes: [] as TechnicalPassportRoute[],
  measurements: [] as TechnicalPassportMeasurement[],
  machines: [] as TechnicalPassportMachine[],
  qc_items: [] as TechnicalPassportQcItem[],
  packaging: [] as TechnicalPassportPackaging[],
  labels: [] as TechnicalPassportLabel[],
  threads: [] as TechnicalPassportThread[],
  dimensions: { ...initialDimensions } as TechnicalPassportDimensions,
  elements: defaultSockElements(initialDimensions) as TechnicalPassportElement[],
  yarns: defaultSockYarns() as TechnicalPassportYarn[],
  costing: [] as TechnicalPassportCosting[],
  attachments: [] as TechnicalPassportAttachment[],
})

const targetProductId = computed(() =>
  isEditing.value ? passport.value?.product_id ?? 0 : productId.value,
)
const backPath = computed(() =>
  targetProductId.value ? `/products/${targetProductId.value}` : '/products',
)
const currentUserName = computed(() => auth.user?.name?.trim() || '')

function applyCurrentUserDefaults(): void {
  if (currentUserName.value) {
    form.changed_by = currentUserName.value
  }
  if (currentUserName.value && !form.prepared_by.trim()) {
    form.prepared_by = currentUserName.value
  }
}

watch(currentUserName, () => {
  applyCurrentUserDefaults()
}, { immediate: true })

function validate(): boolean {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key])
  if (!targetProductId.value) fieldErrors.product = 'A valid product is required.'
  if (!form.template_type) fieldErrors.template_type = 'Select a template.'
  if (!form.sock_type) fieldErrors.sock_type = 'Select a sock type.'
  if (!Number.isFinite(Number(form.sock_weight_gram)) || Number(form.sock_weight_gram) <= 0) {
    fieldErrors.sock_weight_gram = 'Sock weight must be greater than zero.'
  }
  if (!form.change_description.trim()) {
    fieldErrors.change_description = 'Describe this revision.'
  }
  if (!form.changed_by.trim()) fieldErrors.changed_by = 'Enter the author.'
  if (form.status === 'approved') {
    if (!form.rubber_return_place.trim()) fieldErrors.legacy_rubber_return_place = t('engineering.passportValidation.requiredField', { field: t('engineering.passportForm.rubberReturnPlace') })
    if (!form.cotton_yarn.trim()) fieldErrors.legacy_cotton_yarn = t('engineering.passportValidation.requiredField', { field: t('engineering.passportForm.cottonYarn') })
    if (!form.spandex_yarn.trim()) fieldErrors.legacy_spandex_yarn = t('engineering.passportValidation.requiredField', { field: t('engineering.passportForm.spandexYarn') })
    if (!form.rubber_yarn.trim()) fieldErrors.legacy_rubber_yarn = t('engineering.passportValidation.requiredField', { field: t('engineering.passportForm.rubberYarn') })
    if (!form.nylon_yarn.trim()) fieldErrors.legacy_nylon_yarn = t('engineering.passportValidation.requiredField', { field: t('engineering.passportForm.nylonYarn') })
    if (!form.technologist_id) fieldErrors.technologist_id = t('engineering.passportValidation.approverRequired')
    if (!form.shift_manager_id) fieldErrors.shift_manager_id = t('engineering.passportValidation.approverRequired')
    if (!form.manager_id) fieldErrors.manager_id = t('engineering.passportValidation.approverRequired')
  }
  form.raw_materials.forEach((row, index) => {
    if (!row.material_product_id || row.material_product_id <= 0) {
      fieldErrors[`material_${index}`] = t('engineering.passportValidation.materialId')
    } else if (!row.consumption_per_pair || row.consumption_per_pair <= 0) {
      fieldErrors[`material_${index}`] = t('engineering.passportValidation.consumption')
    }
  })
  Object.entries(form.dimensions).forEach(([key, value]) => {
    if (value !== null && value !== undefined && Number(value) < 0) {
      fieldErrors[`dimension_${key}`] = t('engineering.passportValidation.dimensions')
    }
  })
  form.elements.forEach((row, index) => {
    if (!row.element_type || !row.position_code) {
      fieldErrors[`element_${index}`] = t('engineering.passportValidation.element')
    }
  })
  form.yarns.forEach((row, index) => {
    if (!row.role || !row.yarn_type?.trim()) {
      fieldErrors[`yarn_${index}`] = t('engineering.passportValidation.yarn')
    }
  })
  form.routes.forEach((row, index) => {
    if (!row.operation_name?.trim()) {
      fieldErrors[`route_${index}`] = t('engineering.passportValidation.operation')
    } else if (
      row.standard_time_minutes !== undefined &&
      row.standard_time_minutes < 0
    ) {
      fieldErrors[`route_${index}`] = t('engineering.passportValidation.standardTime')
    }
  })
  form.measurements.forEach((row, index) => {
    if (!row.code?.trim() || !row.description?.trim() || !row.measurement_value?.trim()) {
      fieldErrors[`measurement_${index}`] = t('engineering.passportValidation.measurement')
    }
  })
  form.machines.forEach((row, index) => {
    if ((!row.equipment_reference_id || row.equipment_reference_id <= 0) && !row.machine_name?.trim()) {
      fieldErrors[`machine_${index}`] = t('engineering.passportValidation.equipment')
    } else if (!row.needle_count || row.needle_count <= 0) {
      fieldErrors[`machine_${index}`] = t('engineering.passportValidation.needles')
    }
  })
  if (!form.machines.length) {
    fieldErrors.machine_0 = 'Add at least one compatible machine.'
  }
  form.qc_items.forEach((row, index) => {
    if (!row.item_name?.trim() || !row.stage?.trim()) {
      fieldErrors[`qc_${index}`] = t('engineering.passportValidation.qc')
    }
  })
  form.packaging.forEach((row, index) => {
    if (
      !row.packaging_product_id &&
      !row.packaging_reference_id &&
      !row.packaging_name?.trim()
    ) {
      fieldErrors[`packaging_${index}`] = t('engineering.passportValidation.packaging')
    } else if (!row.quantity || row.quantity <= 0) {
      fieldErrors[`packaging_${index}`] = t('engineering.passportValidation.quantity')
    }
  })
  form.labels.forEach((row, index) => {
    if (!row.label_name?.trim()) fieldErrors[`label_${index}`] = t('engineering.passportValidation.label')
  })
  form.threads.forEach((row, index) => {
    if (!row.thread_color_name?.trim() || !row.thread_code?.trim()) {
      fieldErrors[`thread_${index}`] = t('engineering.passportValidation.thread')
    }
  })
  form.costing.forEach((row, index) => {
    if (!row.material_name?.trim()) {
      fieldErrors[`costing_${index}`] = t('engineering.passportValidation.materialName')
    } else if ((row.consumption_per_pair ?? 0) < 0 || (row.waste_percent ?? 0) < 0 || (row.unit_cost ?? 0) < 0) {
      fieldErrors[`costing_${index}`] = t('engineering.passportValidation.costs')
    }
  })
  const firstInvalidTab = tabs.value.find(tabHasErrors)
  if (firstInvalidTab) activeTab.value = firstInvalidTab.id
  return Object.keys(fieldErrors).length === 0
}

function addMaterial(): void {
  form.raw_materials.push({
    stage_code: 'knitting',
    material_product_id: undefined,
    material_name_snapshot: '',
    consumption_per_pair: 0,
    waste_percent: 0,
    uom: 'kg',
    is_required_for_start: true,
    sort_order: form.raw_materials.length + 1,
  })
}

function removeMaterial(index: number): void {
  form.raw_materials.splice(index, 1)
}

function openProductPicker(target: 'material' | 'packaging' | 'label', index: number): void {
  productPicker.target = target
  productPicker.index = index
  productPicker.open = true
}

function openPersonnelPicker(target: 'technologist' | 'shift_manager' | 'manager'): void {
  personnelPicker.target = target
  personnelPicker.open = true
}

function selectedPersonnelName(id?: number): string {
  if (!id) return ''
  const employee = approvalPeople.value.find((person) => Number(person.user_id ?? person.id) === id)
  return employee ? `${employee.first_name} ${employee.last_name}`.trim() : ''
}

function selectApprovalPerson(employee: ProductionEmployee): void {
  const approverId = employee.user_id ?? employee.id
  if (personnelPicker.target === 'technologist') {
    form.technologist_id = approverId
  } else if (personnelPicker.target === 'shift_manager') {
    form.shift_manager_id = approverId
  } else if (personnelPicker.target === 'manager') {
    form.manager_id = approverId
  }
  personnelPicker.open = false
}

function selectProduct(product: EngineeringProduct): void {
  if (productPicker.target === 'material') {
    const row = form.raw_materials[productPicker.index]
    if (row) {
      row.material_product_id = product.id
      row.material_name_snapshot = product.name
      row.uom = product.unit || row.uom
    }
  } else if (productPicker.target === 'packaging') {
    const row = form.packaging[productPicker.index]
    if (row) {
      row.packaging_product_id = product.id
      row.packaging_reference_id = undefined
      row.packaging_name = product.name
    }
  } else if (productPicker.target === 'label') {
    const row = form.labels[productPicker.index]
    if (row) {
      row.label_name = product.name
      row.label_code = product.sku
    }
  }
  productPicker.open = false
}

function addRoute(): void {
  form.routes.push({
    operation_name: '',
    department_agent_id: undefined,
    standard_time_minutes: 0,
    input_unit: 'pair',
    output_unit: 'pair',
    qc_required: false,
    is_required: true,
    sort_order: form.routes.length + 1,
  })
}

function removeRoute(index: number): void {
  form.routes.splice(index, 1)
}

function addMeasurement(): void {
  form.measurements.push({ code: '', description: '', measurement_value: '', measurement_unit: 'cm', sort_order: form.measurements.length + 1 })
}
function removeMeasurement(index: number): void { form.measurements.splice(index, 1) }

function addMachine(): void {
  form.machines.push({ equipment_reference_id: undefined, machine_name: '', needle_count: undefined, is_compatible: true, sort_order: form.machines.length + 1 })
}
function removeMachine(index: number): void { form.machines.splice(index, 1) }
function selectMachine(row: TechnicalPassportMachine): void {
  const equipment = equipmentReferences.value.find(
    (item) => item.id === Number(row.equipment_reference_id),
  )
  row.machine_name = equipment?.name ?? ''
  row.needle_count = equipment?.base_amount && equipment.base_amount > 0
    ? equipment.base_amount
    : undefined
}
function openMachinePicker(index: number): void {
  machinePicker.index = index
  machinePicker.open = true
}
function selectMachineReference(equipment: EquipmentReference): void {
  const row = form.machines[machinePicker.index]
  if (!row) return
  row.equipment_reference_id = equipment.id
  selectMachine(row)
  machinePicker.open = false
}
function enableManualMachineEntry(row: TechnicalPassportMachine): void {
  row.equipment_reference_id = undefined
  if (!row.machine_name?.trim()) {
    row.machine_name = ''
  }
}
function isManualMachine(row: TechnicalPassportMachine): boolean {
  return !row.equipment_reference_id
}

function addQcItem(): void {
  form.qc_items.push({ stage: 'knitting', item_name: '', acceptable_value: '', inspection_method: '', notes: '', is_required: true, sort_order: form.qc_items.length + 1 })
}
function removeQcItem(index: number): void { form.qc_items.splice(index, 1) }

function addPackaging(): void {
  form.packaging.push({ packaging_product_id: undefined, packaging_reference_id: undefined, packaging_name: '', quantity: 1, sort_order: form.packaging.length + 1 })
}
function removePackaging(index: number): void { form.packaging.splice(index, 1) }

function addLabel(): void {
  form.labels.push({ label_name: '', label_code: '', is_required: true, sort_order: form.labels.length + 1 })
}
function removeLabel(index: number): void { form.labels.splice(index, 1) }

function addThread(): void {
  form.threads.push({ thread_color_name: '', thread_code: '', thread_code_id: undefined, thread_code_master: null, sort_order: form.threads.length + 1 })
}
function removeThread(index: number): void { form.threads.splice(index, 1) }
function openThreadColorPicker(index: number): void {
  threadColorPicker.index = index
  threadColorPicker.open = true
}
function selectThreadColor(color: ColorReference): void {
  const row = form.threads[threadColorPicker.index]
  if (!row) return
  row.thread_color_name = color.name
  row.thread_code = color.code || row.thread_code
  threadColorPicker.open = false
}
function openThreadCodePicker(index: number): void {
  threadCodePicker.index = index
  threadCodePicker.open = true
}
function selectThreadCode(threadCode: ThreadCodeReference): void {
  const row = form.threads[threadCodePicker.index]
  if (!row) return
  row.thread_code_id = threadCode.id
  row.thread_code = threadCode.code
  row.thread_code_master = threadCode
  threadCodePicker.open = false
}
function addThreadCodeReference(threadCode: ThreadCodeReference): void {
  if (!threadCodeReferences.value.some((item) => item.id === threadCode.id)) {
    threadCodeReferences.value = [threadCode, ...threadCodeReferences.value]
  }
}

function addCosting(): void {
  form.costing.push({ material_name: '', consumption_per_pair: 0, waste_percent: 0, final_consumption: 0, unit_cost: 0, cost_per_pair: 0, sort_order: form.costing.length + 1 })
}
function removeCosting(index: number): void { form.costing.splice(index, 1) }

function addAttachment(): void {
  form.attachments.push({ attachment_type: 'other', file_name: '', file_url: '', mime_type: '', notes: '', sort_order: form.attachments.length + 1 })
}
function removeAttachment(index: number): void { form.attachments.splice(index, 1) }
function machineDesignFileUrls(): string[] {
  return form.attachments
    .filter((row) => row.attachment_type === 'machine_file')
    .map((row) => row.file_url?.trim() || '')
    .filter(Boolean)
}
function applyAttachmentFile(file: File, row: TechnicalPassportAttachment): void {
  row.file_name = file.name
  row.mime_type = file.type || row.mime_type
  row.file_size = file.size
  if (!row.file_url?.trim()) {
    row.file_url = ''
  }
}
function isSupportedInlineImage(file: File): boolean {
  return ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
        return
      }
      reject(new Error(t('engineering.passportForm.fileReadError')))
    }
    reader.onerror = () => reject(new Error(t('engineering.passportForm.fileReadError')))
    reader.readAsDataURL(file)
  })
}

async function applyMainProductImageFile(file: File): Promise<void> {
  if (!isSupportedInlineImage(file)) {
    submitError.value = t('engineering.passportForm.imageUploadTypeError')
    mainProductImageStatus.value = ''
    return
  }
  try {
    mainProductImageStatus.value = t('engineering.passportForm.imageQueued')
    pendingMainProductImageBase64.value = await readFileAsDataUrl(file)
    pendingMainProductImagePreview.value = pendingMainProductImageBase64.value
    form.main_product_image = file.name
    submitError.value = ''
    mainProductImageStatus.value = t('engineering.passportForm.imageQueuedSuccess')
  } catch (error: unknown) {
    mainProductImageStatus.value = ''
    submitError.value = formatApiError(error)
  }
}
async function applyTechnicalDrawingFile(file: File): Promise<void> {
  if (!isSupportedInlineImage(file)) {
    submitError.value = t('engineering.passportForm.imageUploadTypeError')
    technicalDrawingStatus.value = ''
    return
  }
  try {
    technicalDrawingStatus.value = t('engineering.passportForm.imageQueued')
    pendingTechnicalDrawingBase64.value = await readFileAsDataUrl(file)
    pendingTechnicalDrawingPreview.value = pendingTechnicalDrawingBase64.value
    form.technical_drawing_image = file.name
    submitError.value = ''
    technicalDrawingStatus.value = t('engineering.passportForm.imageQueuedSuccess')
  } catch (error: unknown) {
    technicalDrawingStatus.value = ''
    submitError.value = formatApiError(error)
  }
}

function payload(): TechnicalPassportPayload {
  const primaryMachine = form.machines.find((machine) => machine.is_compatible !== false)
    ?? form.machines[0]
  return {
    product_id: targetProductId.value,
    product_name: product.value?.name ?? passport.value?.product_name,
    product_type: product.value?.type ?? passport.value?.product_type,
    template_type: form.template_type,
    sock_type: form.sock_type,
    season: form.season.trim() || undefined,
    size_range: form.size_range.trim() || undefined,
    sock_weight_gram: Number(form.sock_weight_gram),
    machine_type: primaryMachine?.machine_name?.trim()
      || `Equipment #${Number(primaryMachine?.equipment_reference_id)}`,
    machine_needle_count: Number(primaryMachine?.needle_count),
    rubber_return_place: form.rubber_return_place.trim() || undefined,
    cotton_yarn: form.cotton_yarn.trim() || undefined,
    spandex_yarn: form.spandex_yarn.trim() || undefined,
    rubber_yarn: form.rubber_yarn.trim() || undefined,
    nylon_yarn: form.nylon_yarn.trim() || undefined,
    main_product_image: form.main_product_image.trim() || undefined,
    main_product_image_base64: pendingMainProductImageBase64.value || undefined,
    technical_drawing_image: form.technical_drawing_image.trim() || undefined,
    technical_drawing_image_base64: pendingTechnicalDrawingBase64.value || undefined,
    knitting_machine_design_files: machineDesignFileUrls(),
    description: form.description.trim() || undefined,
    prepared_by: form.prepared_by.trim() || form.changed_by.trim(),
    technologist_id: form.technologist_id ? Number(form.technologist_id) : undefined,
    shift_manager_id: form.shift_manager_id ? Number(form.shift_manager_id) : undefined,
    manager_id: form.manager_id ? Number(form.manager_id) : undefined,
    technologist_approved_at: form.technologist_approved_at || undefined,
    shift_manager_approved_at: form.shift_manager_approved_at || undefined,
    manager_approved_at: form.manager_approved_at || undefined,
    reject_reason: form.reject_reason.trim() || undefined,
    status: form.status,
    revisions: [
      ...(isEditing.value ? passport.value?.revisions ?? [] : []),
      {
        revision_no: form.revision_no.trim(),
        revision_date: form.revision_date,
        changed_by: form.changed_by.trim(),
        change_description: form.change_description.trim(),
        status: form.status,
        sort_order: (passport.value?.revisions?.length ?? 0) + 1,
      },
    ],
    raw_materials: form.raw_materials.map((row, index) => ({
      ...row,
      material_product_id: Number(row.material_product_id),
      consumption_per_pair: Number(row.consumption_per_pair),
      waste_percent: Number(row.waste_percent ?? 0),
      sort_order: index + 1,
    })),
    routes: form.routes.map((row, index) => ({
      ...row,
      department_agent_id: row.department_agent_id
        ? Number(row.department_agent_id)
        : undefined,
      standard_time_minutes: Number(row.standard_time_minutes ?? 0),
      sort_order: index + 1,
    })),
    measurements: form.measurements.map((row, index) => ({
      ...row,
      row_count: row.row_count ? Number(row.row_count) : undefined,
      sort_order: index + 1,
    })),
    machines: form.machines.map((row, index) => ({
      ...row,
      equipment_reference_id: Number(row.equipment_reference_id),
      needle_count: Number(row.needle_count),
      sort_order: index + 1,
    })),
    qc_items: form.qc_items.map((row, index) => ({ ...row, sort_order: index + 1 })),
    packaging: form.packaging.map((row, index) => ({
      ...row,
      packaging_product_id: row.packaging_product_id ? Number(row.packaging_product_id) : undefined,
      packaging_reference_id: row.packaging_reference_id ? Number(row.packaging_reference_id) : undefined,
      quantity: Number(row.quantity),
      sort_order: index + 1,
    })),
    labels: form.labels.map((row, index) => ({ ...row, sort_order: index + 1 })),
    threads: form.threads.map((row, index) => ({
      ...row,
      thread_code_id: row.thread_code_id ? Number(row.thread_code_id) : undefined,
      sort_order: index + 1,
    })),
    dimensions: Object.fromEntries(
      Object.entries(form.dimensions).map(([key, value]) => [
        key,
        value === null || value === undefined ? null : Number(value),
      ]),
    ) as TechnicalPassportDimensions,
    elements: form.elements.map((row, index) => ({
      ...serializeDecorativeElement(row),
      length_mm: row.length_mm === null || row.length_mm === undefined ? null : Number(row.length_mm),
      width_mm: row.width_mm === null || row.width_mm === undefined ? null : Number(row.width_mm),
      height_mm: row.height_mm === null || row.height_mm === undefined ? null : Number(row.height_mm),
      is_required: Boolean(row.is_required),
      sort_order: index + 1,
    })),
    yarns: form.yarns.map((row, index) => ({ ...row, sort_order: index + 1 })),
    costing: form.costing.map((row, index) => ({
      ...row,
      consumption_per_pair: Number(row.consumption_per_pair ?? 0),
      waste_percent: Number(row.waste_percent ?? 0),
      final_consumption: Number(row.final_consumption ?? 0),
      unit_cost: Number(row.unit_cost ?? 0),
      cost_per_pair: Number(row.cost_per_pair ?? 0),
      sort_order: index + 1,
    })),
    attachments: form.attachments
      .filter((row) =>
        Boolean(
          row.file_name?.trim()
          || row.file_url?.trim()
          || row.mime_type?.trim()
          || row.notes?.trim(),
        ),
      )
      .map((row, index) => ({
        ...row,
        file_size: row.file_size ? Number(row.file_size) : undefined,
        sort_order: index + 1,
      })),
  }
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    const dictionariesPromise = getTechnicalPassportDictionaries(client)
    const equipmentPromise = listEquipmentReferences(client)
    const approvalPeoplePromise = listPersonnelEmployees(client).catch(() => [])
    const yarnMaterialsPromise = listProducts(client, { type: 'raw_material', page: 1, per_page: 500 }, false).catch(() => [])
    const colorsPromise = listColorReferences(client).catch(() => [])
    const threadCodesPromise = listThreadCodes(client).catch(() => [])
    if (isEditing.value) {
      passport.value = await getTechnicalPassport(client, passportId.value)
      if (passport.value.status === 'approved') {
        loadError.value = t('engineering.passportValidation.approvedRevision')
        return
      }
      const currentPassport = passport.value
      product.value = await getProduct(client, currentPassport.product_id)
      Object.assign(form, {
        template_type: currentPassport.template_type ?? '',
        sock_type: currentPassport.sock_type ?? '',
        season: currentPassport.season ?? '',
        size_range: currentPassport.size_range ?? '',
        sock_weight_gram: Number(currentPassport.sock_weight_gram ?? 66),
        description: currentPassport.description ?? '',
        prepared_by: currentPassport.prepared_by ?? '',
        rubber_return_place: currentPassport.rubber_return_place ?? '',
        cotton_yarn: currentPassport.cotton_yarn ?? '',
        spandex_yarn: currentPassport.spandex_yarn ?? '',
        rubber_yarn: currentPassport.rubber_yarn ?? '',
        nylon_yarn: currentPassport.nylon_yarn ?? '',
        main_product_image: currentPassport.main_product_image ?? product.value.photo_url ?? '',
        technical_drawing_image: currentPassport.technical_drawing_image ?? '',
        technologist_id: currentPassport.technologist_id ? Number(currentPassport.technologist_id) : undefined,
        shift_manager_id: currentPassport.shift_manager_id ? Number(currentPassport.shift_manager_id) : undefined,
        manager_id: currentPassport.manager_id ? Number(currentPassport.manager_id) : undefined,
        technologist_approved_at: currentPassport.technologist_approved_at ?? '',
        shift_manager_approved_at: currentPassport.shift_manager_approved_at ?? '',
        manager_approved_at: currentPassport.manager_approved_at ?? '',
        reject_reason: currentPassport.reject_reason ?? '',
        status: currentPassport.status,
        revision_no: String((currentPassport.revisions?.length ?? 0) + 1),
        raw_materials: (currentPassport.raw_materials ?? []).map((row) => ({
          ...row,
        })),
        routes: (currentPassport.routes ?? []).map((row) => ({ ...row })),
        measurements: (currentPassport.measurements ?? []).map((row) => ({ ...row })),
        machines: (currentPassport.machines ?? []).map((row) => ({ ...row })),
        qc_items: (currentPassport.qc_items ?? []).map((row) => ({ ...row })),
        packaging: (currentPassport.packaging ?? []).map((row) => ({ ...row })),
        labels: (currentPassport.labels ?? []).map((row) => ({ ...row })),
        threads: (currentPassport.threads ?? []).map((row) => ({ ...row })),
        dimensions: { ...initialDimensions, ...(currentPassport.dimensions ?? {}) },
        elements: (currentPassport.elements?.length
          ? currentPassport.elements
          : defaultSockElements({ ...initialDimensions, ...(currentPassport.dimensions ?? {}) })
        ).map((row) => restoreDecorativeElement({ ...row })),
        yarns: (currentPassport.yarns?.length
          ? currentPassport.yarns
          : defaultSockYarns()
        ).map((row) => ({ ...row })),
        costing: (currentPassport.costing ?? []).map((row) => ({ ...row })),
        attachments: [
          ...(currentPassport.attachments ?? []).map((row) => ({ ...row })),
          ...((currentPassport.knitting_machine_design_files ?? [])
            .filter((value) => value?.trim())
            .filter((value) => !(currentPassport.attachments ?? []).some((row) => row.attachment_type === 'machine_file' && row.file_url?.trim() === value.trim()))
            .map((value, index) => ({
              attachment_type: 'machine_file',
              file_name: value.split('/').pop() || `machine-file-${index + 1}`,
              file_url: value,
              sort_order: (currentPassport.attachments?.length ?? 0) + index + 1,
            }))),
        ],
      })
      applyCurrentUserDefaults()
      pendingMainProductImageBase64.value = ''
      pendingMainProductImagePreview.value = ''
      pendingTechnicalDrawingBase64.value = ''
      pendingTechnicalDrawingPreview.value = ''
      mainProductImageStatus.value = ''
      technicalDrawingStatus.value = ''
    } else {
      if (!Number.isInteger(productId.value) || productId.value <= 0) {
        throw new Error(t('engineering.passportValidation.productId'))
      }
      product.value = await getProduct(client, productId.value)
      form.main_product_image = product.value.photo_url ?? ''
      applyCurrentUserDefaults()
      pendingMainProductImageBase64.value = ''
      pendingMainProductImagePreview.value = ''
      pendingTechnicalDrawingBase64.value = ''
      pendingTechnicalDrawingPreview.value = ''
      mainProductImageStatus.value = ''
      technicalDrawingStatus.value = ''
    }
    try {
      const versions = await listProductBomVersions(client, targetProductId.value)
      const activeVersion = versions.find((version) => Boolean(version.is_active))
      activeBom.value = activeVersion ? await getBomVersion(client, activeVersion.id) : null
      if (activeBom.value) {
        const linked = applyBomToPassport(activeBom.value)
        form.routes = linked.routes
        form.raw_materials = linked.materials
      }
    } catch {
      activeBom.value = null
    }
    templateTypes.value = (await dictionariesPromise).template_types
    equipmentReferences.value = await equipmentPromise
    approvalPeople.value = await approvalPeoplePromise
    yarnMaterials.value = await yarnMaterialsPromise
    colorReferences.value = await colorsPromise
    threadCodeReferences.value = await threadCodesPromise
    initialFormState.value = deepClone(form as unknown as Record<string, unknown>)
  } catch (error: unknown) {
    loadError.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

async function submit(): Promise<void> {
  if (!validate()) return
  saving.value = true
  submitError.value = ''
  try {
    const result = isEditing.value
      ? await updateTechnicalPassport(client, passportId.value, payload())
      : await createTechnicalPassport(client, payload())
    await router.push(`/technical-passports/${result.id}`)
  } catch (error: unknown) {
    submitError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

async function submitForApproval(): Promise<void> {
  form.status = 'pending_approval'
  await submit()
  if (submitError.value || Object.keys(fieldErrors).length) form.status = 'draft'
}

async function saveDraft(): Promise<void> {
  form.status = 'draft'
  await submit()
}

function approvalTimestamp(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

async function approvePassport(): Promise<void> {
  const previousStatus = form.status
  const previousApprovals = {
    technologist_approved_at: form.technologist_approved_at,
    shift_manager_approved_at: form.shift_manager_approved_at,
    manager_approved_at: form.manager_approved_at,
  }
  const stampedAt = approvalTimestamp()
  form.status = 'approved'
  form.technologist_approved_at = form.technologist_id ? (form.technologist_approved_at || stampedAt) : ''
  form.shift_manager_approved_at = form.shift_manager_id ? (form.shift_manager_approved_at || stampedAt) : ''
  form.manager_approved_at = form.manager_id ? (form.manager_approved_at || stampedAt) : ''
  await submit()
  if (submitError.value || Object.keys(fieldErrors).length) {
    form.status = previousStatus
    form.technologist_approved_at = previousApprovals.technologist_approved_at
    form.shift_manager_approved_at = previousApprovals.shift_manager_approved_at
    form.manager_approved_at = previousApprovals.manager_approved_at
  }
}

async function rejectPassport(): Promise<void> {
  const reason = form.reject_reason.trim()
  if (!reason) {
    submitError.value = t('engineering.passportForm.rejectReasonRequired')
    return
  }
  if (!window.confirm(t('engineering.passportForm.rejectConfirm'))) return
  const previousStatus = form.status
  const previousApprovals = {
    technologist_id: form.technologist_id,
    shift_manager_id: form.shift_manager_id,
    manager_id: form.manager_id,
    technologist_approved_at: form.technologist_approved_at,
    shift_manager_approved_at: form.shift_manager_approved_at,
    manager_approved_at: form.manager_approved_at,
  }
  form.status = 'rejected'
  form.technologist_id = undefined
  form.shift_manager_id = undefined
  form.manager_id = undefined
  form.technologist_approved_at = ''
  form.shift_manager_approved_at = ''
  form.manager_approved_at = ''
  form.change_description = `${form.change_description.trim() ? `${form.change_description.trim()} — ` : ''}${t('engineering.passportForm.rejectReasonLabel')}: ${reason}`
  await submit()
  if (submitError.value || Object.keys(fieldErrors).length) {
    form.status = previousStatus
    form.technologist_id = previousApprovals.technologist_id
    form.shift_manager_id = previousApprovals.shift_manager_id
    form.manager_id = previousApprovals.manager_id
    form.technologist_approved_at = previousApprovals.technologist_approved_at
    form.shift_manager_approved_at = previousApprovals.shift_manager_approved_at
    form.manager_approved_at = previousApprovals.manager_approved_at
  }
}

onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :data-tooltip="$t('engineering.passportDetail.back')" :to="backPath">{{ $t('engineering.passportDetail.back') }}</RouterLink>
    <div class="page-toolbar__actions">
      <button class="secondary-button" type="submit" form="passport-form" :disabled="loading || saving">{{ saving ? $t('engineering.passportForm.saving') : $t('engineering.passportForm.saveDraft') }}</button>
      <button type="button" :disabled="loading || saving" @click="submitForApproval">
        {{ saving && form.status === 'pending_approval' ? $t('engineering.passportForm.submitting') : $t('engineering.passportForm.submitApproval') }}
      </button>
      <button type="button" :disabled="loading || saving" @click="approvePassport">
        {{ saving && form.status === 'approved' ? $t('engineering.passportForm.approving') : $t('engineering.passportForm.approve') }}
      </button>
      <button class="reject-button" type="button" :disabled="loading || saving || form.status === 'approved'" @click="rejectPassport">
        {{ saving && form.status === 'rejected' ? $t('engineering.passportForm.rejecting') : $t('engineering.passportForm.reject') }}
      </button>
    </div>
  </div>
  <div v-if="loading" class="page-state">{{ $t('engineering.passportForm.loading') }}</div>
  <div v-else-if="loadError" class="page-state page-state--error" role="alert">
    <p>{{ loadError }}</p>
    <RouterLink :to="backPath">{{ $t('engineering.passportForm.returnProduct') }}</RouterLink>
  </div>
  <form v-else id="passport-form" class="passport-form" @submit.prevent="saveDraft">
    <header>
      <div>
        <p class="eyebrow">{{ isEditing ? $t('engineering.passportForm.passportNumber', { id: passportId }) : $t('engineering.passportForm.newPassport') }}</p>
        <h2>{{ product?.name }}</h2>
        <p>{{ $t('engineering.passportForm.intro') }}</p>
      </div>
    </header>

    <p v-if="submitError" class="form-error" role="alert">{{ submitError }}</p>
    <nav class="passport-tabs" :aria-label="$t('engineering.passportForm.sectionsAria')" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="passport-tab"
        :class="{ active: activeTab === tab.id, invalid: tabHasErrors(tab) }"
        type="button"
        role="tab"
        :aria-selected="activeTab === tab.id"
        :aria-controls="`passport-panel-${tab.id}`"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}<span v-if="tabHasChanges(tab)" class="tab-change" aria-hidden="true">*</span><span v-if="tabHasErrors(tab)" class="tab-error" :aria-label="$t('engineering.passportForm.errorsAria')">!</span>
      </button>
    </nav>
    <section v-show="activeTab === 'general'" id="passport-panel-general" role="tabpanel">
      <h3>{{ $t('engineering.passportForm.definition') }}</h3>
      <div class="form-grid form-grid--specs">
        <label>{{ $t('engineering.passportForm.template') }} *
          <select v-model="form.template_type">
            <option value="">{{ $t('engineering.passportForm.selectTemplate') }}</option>
            <option v-for="option in templateTypes" :key="option.code" :value="option.code">{{ option.label }}</option>
          </select>
          <small v-if="fieldErrors.template_type">{{ fieldErrors.template_type }}</small>
        </label>
        <label>{{ $t('engineering.passportForm.sockType') }} *
          <select v-model="form.sock_type">
            <option value="">{{ $t('engineering.passportForm.selectSockType') }}</option>
            <option value="uzun">{{ $t('engineering.passportForm.long') }}</option><option value="kalta">{{ $t('engineering.passportForm.short') }}</option><option value="sledi">{{ $t('engineering.passportForm.noShow') }}</option>
          </select>
          <small v-if="fieldErrors.sock_type">{{ fieldErrors.sock_type }}</small>
        </label>
        <label>{{ $t('engineering.passportForm.season') }}<input v-model="form.season" type="text"></label>
        <label>{{ $t('engineering.passportForm.sizeRange') }}<input v-model="form.size_range" type="text" placeholder="36-40"></label>
        <label>{{ $t('engineering.passportForm.sockWeight') }} *
          <input v-model.number="form.sock_weight_gram" type="number" min="0.01" step="0.01">
          <small v-if="fieldErrors.sock_weight_gram">{{ fieldErrors.sock_weight_gram }}</small>
        </label>
        <label class="wide">{{ $t('engineering.passportForm.description') }}<textarea v-model="form.description" rows="3"></textarea></label>
      </div>
      <div class="spec-section">
        <div class="section-heading section-heading--compact">
          <div>
            <h3>{{ $t('engineering.passportForm.requiredSpecifications') }}</h3>
            <p>{{ $t('engineering.passportForm.requiredSpecificationsHelp') }}</p>
          </div>
        </div>
        <div class="form-grid form-grid--specs">
        <label>{{ $t('engineering.passportForm.rubberReturnPlace') }}
          <input v-model="form.rubber_return_place" type="text">
          <small v-if="fieldErrors.legacy_rubber_return_place">{{ fieldErrors.legacy_rubber_return_place }}</small>
        </label>
        <label>{{ $t('engineering.passportForm.cottonYarn') }}
          <input v-model="form.cotton_yarn" type="text">
          <small v-if="fieldErrors.legacy_cotton_yarn">{{ fieldErrors.legacy_cotton_yarn }}</small>
        </label>
        <label>{{ $t('engineering.passportForm.spandexYarn') }}
          <input v-model="form.spandex_yarn" type="text">
          <small v-if="fieldErrors.legacy_spandex_yarn">{{ fieldErrors.legacy_spandex_yarn }}</small>
        </label>
        <label>{{ $t('engineering.passportForm.rubberYarn') }}
          <input v-model="form.rubber_yarn" type="text">
          <small v-if="fieldErrors.legacy_rubber_yarn">{{ fieldErrors.legacy_rubber_yarn }}</small>
        </label>
        <label>{{ $t('engineering.passportForm.nylonYarn') }}
          <input v-model="form.nylon_yarn" type="text">
          <small v-if="fieldErrors.legacy_nylon_yarn">{{ fieldErrors.legacy_nylon_yarn }}</small>
        </label>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'approval'" id="passport-panel-approval" role="tabpanel">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.passportForm.approval') }}</h3>
          <p>{{ $t('engineering.passportForm.approvalHelp') }}</p>
        </div>
      </div>
      <div class="approval-panel">
        <div class="form-grid">
          <label>{{ $t('engineering.passportDetail.technologist') }} *
            <button class="product-select" type="button" @click="openPersonnelPicker('technologist')">
              <span>{{ selectedPersonnelName(form.technologist_id) || $t('engineering.passportForm.selectApprover') }}</span>
              <small>{{ form.technologist_id ? (selectedPersonnelName(form.technologist_id) || $t('engineering.passportForm.selectApprover')) : $t('engineering.passportForm.browsePersonnel') }}</small>
            </button>
            <small v-if="fieldErrors.technologist_id">{{ fieldErrors.technologist_id }}</small>
          </label>
          <label>{{ $t('engineering.passportDetail.shiftManager') }} *
            <button class="product-select" type="button" @click="openPersonnelPicker('shift_manager')">
              <span>{{ selectedPersonnelName(form.shift_manager_id) || $t('engineering.passportForm.selectApprover') }}</span>
              <small>{{ form.shift_manager_id ? (selectedPersonnelName(form.shift_manager_id) || $t('engineering.passportForm.selectApprover')) : $t('engineering.passportForm.browsePersonnel') }}</small>
            </button>
            <small v-if="fieldErrors.shift_manager_id">{{ fieldErrors.shift_manager_id }}</small>
          </label>
          <label>{{ $t('engineering.passportDetail.manager') }} *
            <button class="product-select" type="button" @click="openPersonnelPicker('manager')">
              <span>{{ selectedPersonnelName(form.manager_id) || $t('engineering.passportForm.selectApprover') }}</span>
              <small>{{ form.manager_id ? (selectedPersonnelName(form.manager_id) || $t('engineering.passportForm.selectApprover')) : $t('engineering.passportForm.browsePersonnel') }}</small>
            </button>
            <small v-if="fieldErrors.manager_id">{{ fieldErrors.manager_id }}</small>
          </label>
          <label class="wide">{{ $t('engineering.passportForm.rejectReasonLabel') }}
            <textarea v-model="form.reject_reason" rows="2" :placeholder="t('engineering.passportForm.rejectReasonPlaceholder')"></textarea>
          </label>
        </div>
      </div>
      <article v-if="submitError || approvalRecommendations.length" class="approval-diagnostics">
        <div class="approval-diagnostics__head">
          <strong>{{ approvalErrorTitle || $t('engineering.passportForm.approvalChecklist') }}</strong>
          <span>{{ $t('engineering.passportForm.approvalRecommendations') }}</span>
        </div>
        <p v-if="submitError" class="approval-diagnostics__message">{{ submitError }}</p>
        <ul v-if="approvalRecommendations.length" class="approval-diagnostics__list">
          <li v-for="recommendation in approvalRecommendations" :key="recommendation">{{ recommendation }}</li>
        </ul>
      </article>
    </section>

    <div v-show="activeTab === 'structure'" id="passport-panel-structure" class="structure-panel" role="tabpanel">
      <SockPassportDimensionsTable :dimensions="form.dimensions" />
      <SockPassportElementsTable
        :dimensions="form.dimensions"
        :rows="form.elements"
        :yarns="form.yarns"
        :sock-type="form.sock_type"
        :template-type="form.template_type"
      />
      <SockPassportYarnsTable
        :rows="form.yarns"
        :materials="yarnMaterials"
        :colors="colorReferences"
      />
      <p v-if="Object.keys(fieldErrors).some((key) => key.startsWith('dimension_') || key.startsWith('element_') || key.startsWith('yarn_'))" class="form-error" role="alert">
        {{ $t('engineering.passportForm.structureError') }}
      </p>
    </div>

    <section v-show="activeTab === 'materials'" id="passport-panel-materials" role="tabpanel">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.passportForm.rawMaterials') }}</h3>
          <p>{{ $t('engineering.passportForm.materialsHelp') }}</p>
        </div>
        <button v-if="!activeBom" class="secondary-button" type="button" @click="addMaterial">{{ $t('engineering.passportForm.addMaterial') }}</button>
        <RouterLink v-else class="secondary-link" :to="`/products/${targetProductId}/bom/${activeBom.id}/edit`">{{ $t('engineering.bomLink.edit') }}</RouterLink>
      </div>
      <div v-if="activeBom" class="bom-link-notice">
        <strong>{{ $t('engineering.bomLink.notice', { version: activeBom.version_number }) }}</strong>
        <span>{{ $t('engineering.bomLink.snapshot') }}</span>
      </div>
      <fieldset :disabled="Boolean(activeBom)" class="linked-fieldset">
      <div v-if="!form.raw_materials.length" class="empty-row">{{ $t('engineering.passportForm.noMaterials') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.raw_materials" :key="index" class="repeatable-row material-row">
          <label>{{ $t('engineering.passportForm.stage') }}
            <select v-model="row.stage_code">
              <option value="knitting">{{ $t('engineering.manufacturingStage.knitting') }}</option><option value="rosso">{{ $t('engineering.manufacturingStage.rosso') }}</option>
              <option value="boarding">{{ $t('engineering.manufacturingStage.boarding') }}</option><option value="labeling">{{ $t('engineering.manufacturingStage.labeling') }}</option>
              <option value="packaging">{{ $t('engineering.manufacturingStage.packaging') }}</option><option value="finishing">{{ $t('engineering.manufacturingStage.finishing') }}</option>
            </select>
          </label>
          <label>{{ $t('engineering.passportDetail.material') }} *
            <button class="product-select" type="button" @click="openProductPicker('material', index)">
              <span>{{ row.material_name_snapshot || $t('engineering.passportForm.selectRawMaterial') }}</span>
              <small>{{ row.material_product_id ? `#${row.material_product_id}` : $t('engineering.passportForm.browseProducts') }}</small>
            </button>
          </label>
          <label>{{ $t('engineering.passportForm.materialName') }}<input v-model="row.material_name_snapshot" readonly type="text"></label>
          <label>{{ $t('engineering.passportForm.consumptionPair') }} *<input v-model.number="row.consumption_per_pair" min="0.000001" step="0.000001" type="number"></label>
          <label>{{ $t('engineering.passportForm.waste') }}<input v-model.number="row.waste_percent" min="0" step="0.01" type="number"></label>
          <label>{{ $t('engineering.passportForm.unit') }}<input v-model="row.uom" type="text"></label>
          <button class="remove-button" type="button" @click="removeMaterial(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`material_${index}`]" class="row-error">{{ fieldErrors[`material_${index}`] }}</small>
        </div>
      </div>
      </fieldset>
    </section>

    <section v-show="activeTab === 'route'" id="passport-panel-route" role="tabpanel">
      <div class="section-heading">
        <div>
          <h3>{{ $t('engineering.passportDetail.productionRoute') }}</h3>
          <p>{{ $t('engineering.passportForm.routeHelp') }}</p>
        </div>
        <button v-if="!activeBom" class="secondary-button" type="button" @click="addRoute">{{ $t('engineering.passportForm.addOperation') }}</button>
        <RouterLink v-else class="secondary-link" :to="`/products/${targetProductId}/bom/${activeBom.id}/edit`">{{ $t('engineering.bomLink.edit') }}</RouterLink>
      </div>
      <div v-if="activeBom" class="bom-link-notice">
        <strong>{{ $t('engineering.bomLink.notice', { version: activeBom.version_number }) }}</strong>
        <span>{{ $t('engineering.bomLink.snapshot') }}</span>
      </div>
      <fieldset :disabled="Boolean(activeBom)" class="linked-fieldset">
      <div v-if="!form.routes.length" class="empty-row">{{ $t('engineering.passportForm.noRoutes') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.routes" :key="index" class="repeatable-row route-row">
          <span class="sequence">{{ index + 1 }}</span>
          <label>{{ $t('engineering.passportForm.operationName') }} *<input v-model="row.operation_name" type="text"></label>
          <label>{{ $t('engineering.passportForm.departmentId') }}<input v-model.number="row.department_agent_id" min="1" type="number"></label>
          <label>{{ $t('engineering.passportForm.standardMinutes') }}<input v-model.number="row.standard_time_minutes" min="0" step="0.01" type="number"></label>
          <label>{{ $t('engineering.passportForm.inputUnit') }}<input v-model="row.input_unit" type="text"></label>
          <label>{{ $t('engineering.passportForm.outputUnit') }}<input v-model="row.output_unit" type="text"></label>
          <label class="check-label"><input v-model="row.qc_required" type="checkbox"> {{ $t('engineering.passportForm.qcRequired') }}</label>
          <button class="remove-button" type="button" @click="removeRoute(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`route_${index}`]" class="row-error">{{ fieldErrors[`route_${index}`] }}</small>
        </div>
      </div>
      </fieldset>
    </section>

    <section v-show="activeTab === 'measurements'" id="passport-panel-measurements" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.measurements') }}</h3><p>{{ $t('engineering.passportForm.measurementsHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addMeasurement">{{ $t('engineering.passportForm.addMeasurement') }}</button>
      </div>
      <div v-if="!form.measurements.length" class="empty-row">{{ $t('engineering.passportForm.noMeasurements') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.measurements" :key="index" class="repeatable-row measurement-row">
          <label>{{ $t('engineering.passportForm.code') }} *<input v-model="row.code" type="text" placeholder="L1"></label>
          <label>{{ $t('engineering.passportForm.description') }} *<input v-model="row.description" type="text"></label>
          <label>{{ $t('engineering.passportForm.value') }} *<input v-model="row.measurement_value" type="text" placeholder="24 ± 1"></label>
          <label>{{ $t('engineering.passportForm.unit') }}<input v-model="row.measurement_unit" type="text"></label>
          <label>{{ $t('engineering.passportForm.rows') }}<input v-model.number="row.row_count" min="1" type="number"></label>
          <button class="remove-button" type="button" @click="removeMeasurement(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`measurement_${index}`]" class="row-error">{{ fieldErrors[`measurement_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'machines'" id="passport-panel-machines" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.compatibleMachines') }}</h3><p>{{ $t('engineering.passportForm.machinesHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addMachine">{{ $t('engineering.passportForm.addMachine') }}</button>
      </div>
      <div v-if="!form.machines.length" class="empty-row">{{ $t('engineering.passportForm.noMachines') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.machines" :key="index" class="repeatable-row machine-row">
          <label>{{ $t('engineering.passportForm.machine') }}
            <button class="product-select" type="button" @click="openMachinePicker(index)">
              <span>{{ row.machine_name || $t('engineering.passportForm.selectMachine') }}</span>
              <small>{{ row.equipment_reference_id ? `#${row.equipment_reference_id}` : $t('engineering.passportForm.browseProducts') }}</small>
            </button>
          </label>
          <label>{{ $t('engineering.passportForm.machine') }}<input :value="row.equipment_reference_id ?? ''" type="text" readonly></label>
          <label>{{ $t('engineering.passportForm.machineName') }} *<input v-model="row.machine_name" :readonly="!isManualMachine(row)" type="text"></label>
          <label>{{ $t('engineering.passportForm.needleCount') }} *<input v-model.number="row.needle_count" min="1" type="number"></label>
          <label class="check-label"><input v-model="row.is_compatible" type="checkbox"> {{ $t('engineering.passportForm.compatible') }}</label>
          <button class="secondary-button secondary-button--inline" type="button" @click="enableManualMachineEntry(row)">{{ $t('engineering.passportForm.addNewMachine') }}</button>
          <button class="remove-button" type="button" @click="removeMachine(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`machine_${index}`]" class="row-error">{{ fieldErrors[`machine_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'quality'" id="passport-panel-quality" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.qualityChecklist') }}</h3><p>{{ $t('engineering.passportForm.qualityHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addQcItem">{{ $t('engineering.passportForm.addQc') }}</button>
      </div>
      <div v-if="!form.qc_items.length" class="empty-row">{{ $t('engineering.passportForm.noQuality') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.qc_items" :key="index" class="repeatable-row qc-row">
          <label>{{ $t('engineering.passportForm.stage') }} *
            <select v-model="row.stage">
              <option value="knitting">{{ $t('engineering.manufacturingStage.knitting') }}</option><option value="rosso">{{ $t('engineering.manufacturingStage.rosso') }}</option>
              <option value="boarding">{{ $t('engineering.manufacturingStage.boarding') }}</option><option value="labeling">{{ $t('engineering.manufacturingStage.labeling') }}</option>
              <option value="packaging">{{ $t('engineering.manufacturingStage.packaging') }}</option><option value="finishing">{{ $t('engineering.manufacturingStage.finishing') }}</option>
            </select>
          </label>
          <label>{{ $t('engineering.passportForm.checkItem') }} *<input v-model="row.item_name" type="text"></label>
          <label>{{ $t('engineering.passportForm.acceptableValue') }}<input v-model="row.acceptable_value" type="text"></label>
          <label>{{ $t('engineering.passportForm.inspectionMethod') }}<input v-model="row.inspection_method" type="text"></label>
          <label>{{ $t('engineering.passportForm.notes') }}<input v-model="row.notes" type="text"></label>
          <label class="check-label"><input v-model="row.is_required" type="checkbox"> {{ $t('engineering.passportForm.required') }}</label>
          <button class="remove-button" type="button" @click="removeQcItem(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`qc_${index}`]" class="row-error">{{ fieldErrors[`qc_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'packaging'" id="passport-panel-packaging" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.packaging') }}</h3><p>{{ $t('engineering.passportForm.packagingHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addPackaging">{{ $t('engineering.passportForm.addPackaging') }}</button>
      </div>
      <div v-if="!form.packaging.length" class="empty-row">{{ $t('engineering.passportForm.noPackaging') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.packaging" :key="index" class="repeatable-row packaging-row">
          <label>{{ $t('engineering.passportForm.packagingProduct') }}
            <button class="product-select" type="button" @click="openProductPicker('packaging', index)">
              <span>{{ row.packaging_name || $t('engineering.passportForm.selectPackaging') }}</span>
              <small>{{ row.packaging_product_id ? `#${row.packaging_product_id}` : $t('engineering.passportForm.browseProducts') }}</small>
            </button>
          </label>
          <label>{{ $t('engineering.passportForm.referenceId') }}<input v-model.number="row.packaging_reference_id" min="1" type="number"></label>
          <label>{{ $t('engineering.passportForm.packagingName') }}<input v-model="row.packaging_name" type="text"></label>
          <label>{{ $t('engineering.passportForm.quantity') }} *<input v-model.number="row.quantity" min="0.000001" step="0.000001" type="number"></label>
          <button class="remove-button" type="button" @click="removePackaging(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`packaging_${index}`]" class="row-error">{{ fieldErrors[`packaging_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'labels'" id="passport-panel-labels" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.labels') }}</h3><p>{{ $t('engineering.passportForm.labelsHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addLabel">{{ $t('engineering.passportForm.addLabel') }}</button>
      </div>
      <div v-if="!form.labels.length" class="empty-row">{{ $t('engineering.passportForm.noLabels') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.labels" :key="index" class="repeatable-row label-row">
          <label>{{ $t('engineering.passportForm.labelProduct') }}
            <button class="product-select" type="button" @click="openProductPicker('label', index)">
              <span>{{ row.label_name || $t('engineering.passportForm.selectLabel') }}</span>
              <small>{{ row.label_code || $t('engineering.passportForm.browseProducts') }}</small>
            </button>
          </label>
          <label>{{ $t('engineering.passportForm.labelName') }} *<input v-model="row.label_name" type="text"></label>
          <label>{{ $t('engineering.passportForm.labelCode') }}<input v-model="row.label_code" type="text"></label>
          <label class="check-label"><input v-model="row.is_required" type="checkbox"> {{ $t('engineering.passportForm.required') }}</label>
          <button class="remove-button" type="button" @click="removeLabel(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`label_${index}`]" class="row-error">{{ fieldErrors[`label_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'threads'" id="passport-panel-threads" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.threads') }}</h3><p>{{ $t('engineering.passportForm.threadsHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addThread">{{ $t('engineering.passportForm.addThread') }}</button>
      </div>
      <div v-if="!form.threads.length" class="empty-row">{{ $t('engineering.passportForm.noThreads') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.threads" :key="index" class="repeatable-row thread-row">
          <label>{{ $t('engineering.passportForm.colorName') }}
            <button class="product-select" type="button" @click="openThreadColorPicker(index)">
              <span>{{ row.thread_color_name || $t('engineering.yarnCatalog.selectColor') }}</span>
              <small>{{ row.thread_code || $t('engineering.passportForm.browseProducts') }}</small>
            </button>
          </label>
          <label>{{ $t('engineering.passportForm.colorName') }} *<input v-model="row.thread_color_name" type="text" readonly></label>
          <label>{{ $t('engineering.passportForm.threadCode') }}
            <button class="product-select" type="button" @click="openThreadCodePicker(index)">
              <span>{{ row.thread_code || $t('engineering.passportForm.threadCode') }}</span>
              <small>{{ row.thread_code_master?.name || $t('engineering.passportForm.browseProducts') }}</small>
            </button>
          </label>
          <label>{{ $t('engineering.passportForm.threadCode') }} *<input v-model="row.thread_code" type="text" readonly></label>
          <button class="remove-button" type="button" @click="removeThread(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`thread_${index}`]" class="row-error">{{ fieldErrors[`thread_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'costing'" id="passport-panel-costing" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.costing') }}</h3><p>{{ $t('engineering.passportForm.costingHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addCosting">{{ $t('engineering.passportForm.addCosting') }}</button>
      </div>
      <div v-if="!form.costing.length" class="empty-row">{{ $t('engineering.passportForm.noCosting') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.costing" :key="index" class="repeatable-row costing-row">
          <label>{{ $t('engineering.passportForm.materialName') }} *<input v-model="row.material_name" type="text"></label>
          <label>{{ $t('engineering.passportForm.consumptionPair') }}<input v-model.number="row.consumption_per_pair" min="0" step="0.000001" type="number"></label>
          <label>{{ $t('engineering.passportForm.waste') }}<input v-model.number="row.waste_percent" min="0" step="0.01" type="number"></label>
          <label>{{ $t('engineering.passportForm.finalConsumption') }}<input v-model.number="row.final_consumption" min="0" step="0.000001" type="number"></label>
          <label>{{ $t('engineering.passportForm.unitCost') }}<input v-model.number="row.unit_cost" min="0" step="0.01" type="number"></label>
          <label>{{ $t('engineering.passportForm.costPair') }}<input v-model.number="row.cost_per_pair" min="0" step="0.01" type="number"></label>
          <button class="remove-button" type="button" @click="removeCosting(index)">{{ $t('engineering.passportForm.remove') }}</button>
          <small v-if="fieldErrors[`costing_${index}`]" class="row-error">{{ fieldErrors[`costing_${index}`] }}</small>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'attachments'" id="passport-panel-attachments" role="tabpanel">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.attachments') }}</h3><p>{{ $t('engineering.passportForm.attachmentsHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addAttachment">{{ $t('engineering.passportForm.addAttachment') }}</button>
      </div>
      <div class="spec-section">
        <div class="form-grid">
          <div class="wide">
            <FileUploadField
              :label="$t('engineering.passportForm.mainProductImage')"
              :model-value="form.main_product_image"
              accept=".png,.jpg,.jpeg,.webp,.gif"
              :button-label="$t('engineering.passportForm.chooseFile')"
              :helper-text="$t('engineering.passportForm.imageUploadHelp')"
              :status-text="mainProductImageStatus"
              :error-text="submitError === $t('engineering.passportForm.imageUploadTypeError') ? submitError : ''"
              @update:model-value="form.main_product_image = $event"
              @selected="applyMainProductImageFile"
            />
            <small v-if="fieldErrors.legacy_main_product_image">{{ fieldErrors.legacy_main_product_image }}</small>
          </div>
          <div class="wide">
            <FileUploadField
              :label="$t('engineering.passportForm.technicalDrawingImage')"
              :model-value="form.technical_drawing_image"
              accept=".png,.jpg,.jpeg,.webp,.gif"
              :button-label="$t('engineering.passportForm.chooseFile')"
              :helper-text="$t('engineering.passportForm.imageUploadHelp')"
              :status-text="technicalDrawingStatus"
              :error-text="submitError === $t('engineering.passportForm.imageUploadTypeError') ? submitError : ''"
              @update:model-value="form.technical_drawing_image = $event"
              @selected="applyTechnicalDrawingFile"
            />
            <small v-if="fieldErrors.legacy_technical_drawing_image">{{ fieldErrors.legacy_technical_drawing_image }}</small>
          </div>
        </div>
      </div>
      <div v-if="!form.attachments.length" class="empty-row">{{ $t('engineering.passportForm.noAttachments') }}</div>
      <div v-else class="repeatable-list">
        <div v-for="(row, index) in form.attachments" :key="index" class="repeatable-row attachment-row">
          <label>{{ $t('engineering.passportForm.type') }}
            <select v-model="row.attachment_type">
              <option value="drawing">{{ $t('engineering.passportForm.drawing') }}</option><option value="machine_file">{{ $t('engineering.passportForm.machineFile') }}</option>
              <option value="specification">{{ $t('engineering.passportForm.specification') }}</option><option value="other">{{ $t('engineering.passportForm.other') }}</option>
            </select>
          </label>
          <div>
            <FileUploadField
              :label="$t('engineering.passportForm.fileName')"
              :model-value="row.file_name"
              :button-label="$t('engineering.passportForm.chooseFile')"
              :helper-text="$t('engineering.passportForm.attachmentUploadHelp')"
              :status-text="row.file_size ? $t('engineering.passportForm.attachmentSelectedStatus', { size: row.file_size }) : ''"
              @update:model-value="row.file_name = $event"
              @selected="(file) => applyAttachmentFile(file, row)"
            />
          </div>
          <label>{{ $t('engineering.passportForm.fileUrl') }}<input v-model="row.file_url" type="url"></label>
          <a
            v-if="resolvedAttachmentUrl(row.file_url)"
            class="attachment-link"
            :href="resolvedAttachmentUrl(row.file_url)"
            target="_blank"
            rel="noreferrer"
          >
            {{ $t('engineering.passportForm.openFile') }}
          </a>
          <label>{{ $t('engineering.passportForm.mimeType') }}<input v-model="row.mime_type" type="text" placeholder="application/pdf"></label>
          <label>{{ $t('engineering.passportForm.fileSize') }}<input v-model.number="row.file_size" min="0" type="number"></label>
          <label>{{ $t('engineering.passportForm.notes') }}<input v-model="row.notes" type="text"></label>
          <button class="remove-button" type="button" @click="removeAttachment(index)">{{ $t('engineering.passportForm.remove') }}</button>
        </div>
      </div>
    </section>

    <section v-show="activeTab === 'revision'" id="passport-panel-revision" role="tabpanel">
      <h3>{{ $t('engineering.passportForm.revisionEntry') }}</h3>
      <div class="form-grid">
        <label>{{ $t('engineering.passportForm.revisionNumber') }} *<input v-model="form.revision_no" required type="text"></label>
        <label>{{ $t('engineering.passportForm.revisionDate') }} *<input v-model="form.revision_date" required type="date"></label>
        <label>{{ $t('engineering.passportForm.changedBy') }} *
          <input v-model="form.changed_by" type="text">
          <small v-if="fieldErrors.changed_by">{{ fieldErrors.changed_by }}</small>
        </label>
        <label>{{ $t('engineering.passportForm.preparedBy') }}<input v-model="form.prepared_by" type="text"></label>
        <label class="wide">{{ $t('engineering.passportForm.changeDescription') }} *
          <textarea v-model="form.change_description" rows="3"></textarea>
          <small v-if="fieldErrors.change_description">{{ fieldErrors.change_description }}</small>
        </label>
      </div>
    </section>
    <ProductPickerModal
      :open="productPicker.open"
      :product-type="'raw_material'"
      :title="productPicker.target === 'packaging'
        ? $t('engineering.passportForm.selectPackaging')
        : productPicker.target === 'label'
          ? $t('engineering.passportForm.selectLabel')
          : $t('engineering.passportForm.selectRawMaterial')"
      @close="productPicker.open = false"
      @select="selectProduct"
    />
    <PersonnelPickerModal
      :open="personnelPicker.open"
      :title="$t('engineering.passportForm.selectApprover')"
      @close="personnelPicker.open = false"
      @select="selectApprovalPerson"
    />
    <EquipmentPickerModal
      :open="machinePicker.open"
      :title="$t('engineering.passportForm.selectMachine')"
      :equipment="equipmentReferences"
      @close="machinePicker.open = false"
      @select="selectMachineReference"
    />
    <ColorPickerModal
      :open="threadColorPicker.open"
      :title="$t('engineering.yarnCatalog.selectColor')"
      :colors="colorReferences"
      @close="threadColorPicker.open = false"
      @select="selectThreadColor"
    />
    <ThreadCodePickerModal
      :open="threadCodePicker.open"
      :title="$t('engineering.passportForm.threadCode')"
      :items="threadCodeReferences"
      @close="threadCodePicker.open = false"
      @created="addThreadCodeReference"
      @select="selectThreadCode"
    />
  </form>
</template>

<style scoped>
.back-link { display: inline-block; margin-bottom: 20px; color: #285b9a; font-weight: 700; }
.page-state { display: grid; min-height: 240px; place-items: center; }
.page-state--error, .form-error, label small { color: #852d22; }
.passport-form { display: grid; gap: 16px; }
.structure-panel { display: grid; gap: 14px; }
.passport-tabs { display: flex; max-width: 100%; margin-bottom: -16px; overflow-x: auto; border-bottom: 1px solid #b9c5c1; scrollbar-width: thin; overscroll-behavior-inline: contain; }
.passport-tab { flex: 0 0 auto; min-height: 38px; padding: 8px 12px; border: 0; border-bottom: 2px solid transparent; color: #53625d; background: transparent; font-size: 12px; white-space: nowrap; }
.passport-tab:hover { color: #173454; }
.passport-tab.active { border-bottom-color: #07996f; border-top-left-radius: 8px; border-top-right-radius: 8px; border-bottom-left-radius: 0 !important; border-bottom-right-radius: 0 !important; color: #173454; background: #fff; }
.passport-tab.invalid { color: #852d22; }
.passport-tab:focus-visible { position: relative; z-index: 1; outline: 2px solid #3568a8; outline-offset: -2px; }
.tab-error { display: inline-grid; width: 17px; height: 17px; margin-left: 6px; border-radius: 50%; color: #fff; background: #b44b3d; font-size: 11px; place-items: center; }
.tab-change { margin-left: 4px; color: #07996f; font-size: 13px; font-weight: 800; }
.backend-help-card { gap: 10px; background: #fbfcfb; }
.backend-help-card--current { border-color: #c87b2b; background: #fff8ef; }
.backend-help-card__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.backend-help-card__definition { margin: 0; color: #31433e; }
.backend-help-card__list { display: grid; gap: 6px; margin: 0; padding-left: 18px; color: #31433e; }
.backend-help-badge { display: inline-flex; align-items: center; min-height: 24px; padding: 0 10px; border-radius: 999px; color: #8a4b0f; background: #f8ddbe; font-size: 12px; font-weight: 700; }
header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.header-actions { display: flex; gap: 10px; }
.eyebrow { margin: 0 0 2px; color: #66726e; font-size: 11px; font-weight: 700; text-transform: uppercase; }
h2 { margin: 0; font-size: 22px; line-height: 1.2; } header p:last-child { margin: 3px 0 0; color: #66726e; font-size: 13px; }
section { padding: 22px; border: 1px solid #d7dfdc; background: #fff; }
h3 { margin: 0 0 18px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.section-heading--compact { margin-bottom: 12px; }
.section-heading h3 { margin-bottom: 4px; }
.section-heading p { margin: 0; color: #66726e; font-size: 13px; }
.spec-section { display: grid; gap: 12px; margin-top:20px; }
.approval-panel { padding: 18px; border: 1px solid #d7dfdc; background: #f8faf9; }
.approval-diagnostics { display: grid; gap: 10px; margin-top: 16px; padding: 18px; border: 1px solid #e5c7c2; background: #fff7f5; }
.approval-diagnostics__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.approval-diagnostics__head strong { color: #852d22; }
.approval-diagnostics__head span { color: #66726e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
.approval-diagnostics__message { margin: 0; color: #852d22; line-height: 1.5; }
.approval-diagnostics__list { display: grid; gap: 6px; margin: 0; padding-left: 18px; color: #53625d; }
.attachment-diagnostics { margin-top: 0; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.form-grid--specs { grid-template-columns: repeat(4, minmax(0, 1fr)); }
label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
.wide { grid-column: 1 / -1; }
input, select, textarea { width: 100%; box-sizing: border-box; padding: 9px 10px; border: 1px solid #b9c5c1; font: inherit; }
button { padding: 10px 16px; border: 0; color: #fff; background: #3568a8; font-weight: 700; }
button:disabled { opacity: .6; }
.reject-button { color: #fff; background: #b0301e; }
.reject-button:hover:not(:disabled) { background: #8f2517; }
.secondary-button { border: 1px solid #b9c5c1; color: #285b9a; background: #fff; }
.secondary-button--inline { align-self: end; }
.product-select { display: flex; align-items: center; justify-content: space-between; min-height: 40px; padding: 8px 10px; border: 1px solid #aebdcc; color: #173454; background: #fff; text-align: left; }
.product-select small { color: #66726e; font-weight: 500; }
.repeatable-list { display: grid; gap: 10px; margin-top: 18px; }
.repeatable-list--compact { margin-top: 10px; }
.repeatable-row { position: relative; display: grid; gap: 12px; padding: 16px; border: 1px solid #d7dfdc; background: #f8faf9; }
.repeatable-row--compact { grid-template-columns: minmax(0, 1fr) auto; padding: 12px; }
.material-row { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.route-row { grid-template-columns: 34px repeat(3, minmax(0, 1fr)); }
.measurement-row, .machine-row, .qc-row, .packaging-row { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.label-row, .thread-row, .costing-row, .attachment-row { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.sequence { display: grid; width: 30px; height: 30px; color: #fff; background: #3568a8; font-weight: 700; place-items: center; }
.check-label { display: flex; align-items: center; align-self: end; min-height: 38px; }
.check-label input { width: auto; }
.attachment-link { align-self: end; color: #285b9a; font-size: 13px; font-weight: 700; text-decoration: none; }
.remove-button { align-self: end; border: 1px solid #d7a8a1; color: #852d22; background: #fff; }
.row-error { grid-column: 1 / -1; color: #852d22; }
.empty-row { margin-top: 16px; padding: 14px; color: #66726e; background: #f3f6f5; }
.linked-fieldset { min-width: 0; margin: 0; padding: 0; border: 0; }
.linked-fieldset:disabled { opacity: .82; }
.bom-link-notice { display: grid; gap: 4px; margin-top: 16px; padding: 12px 14px; border: 1px solid #9db9d8; color: #173454; background: #f2f7fc; font-size: 13px; }
.bom-link-notice span { color: #53625d; }
.secondary-link { padding: 10px 16px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; font-weight: 700; text-decoration: none; }
@media (max-width: 900px) { .material-row, .route-row, .measurement-row, .machine-row, .qc-row, .packaging-row, .label-row, .thread-row, .costing-row, .attachment-row { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1100px) { .form-grid--specs { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 680px) { header, .section-heading { align-items: stretch; flex-direction: column; } .header-actions { flex-direction: column; } .form-grid, .form-grid--specs, .material-row, .route-row, .measurement-row, .machine-row, .qc-row, .packaging-row, .label-row, .thread-row, .costing-row, .attachment-row { grid-template-columns: 1fr; } .wide { grid-column: auto; } }
</style>
